import React, { useState, useEffect, useCallback } from 'react';
import {
  LayoutDashboard, ShoppingBag, Utensils, Sparkles, Users, BarChart3,
  Bell, Package, Calculator, Settings, ChevronRight, ChevronLeft,
  X, Rocket, CheckCircle2, ArrowRight
} from 'lucide-react';

const STEPS = [
  {
    id: 'welcome',
    icon: Rocket,
    title: 'ugra Paneline Hoş Geldiniz!',
    description: 'Bu rehber size panelin tüm özelliklerini adım adım tanıtacak. Her sayfanın ne işe yaradığını öğrenin ve işletmenizi kolayca yönetin.',
    tip: 'Rehberi istediğiniz zaman okla ilerletebilir veya "Bitir" ile atlayabilirsiniz.',
  },
  {
    id: 'dashboard',
    icon: LayoutDashboard,
    title: 'Genel Bakış',
    description: 'Ana paneliniz. Günlük satışlar, aktif müşteriler, sipariş durumları ve önemli metrikleri tek bakışta görün.',
    tip: 'Kartlara tıklayarak ilgili sayfaya hızlıca geçiş yapabilirsiniz.',
    path: '/app',
  },
  {
    id: 'orders',
    icon: ShoppingBag,
    title: 'Siparişler',
    description: 'Gelen siparişleri gerçek zamanlı takip edin. Bekleyen, hazırlanan ve tamamlanan siparişleri yönetin, masa bazında filtreyin.',
    tip: 'Yeni sipariş geldiğinde bildirim sesi duyacaksınız.',
    path: '/app/orders',
  },
  {
    id: 'products',
    icon: Utensils,
    title: 'Ürünler & Menü',
    description: 'Ürünlerinizi ekleyin, kategorize edin ve fiyatlandırın. Menü editörü ile dijital menünüzü oluşturun — müşteriler QR kod ile görebilir.',
    tip: 'Ürün görselleri eklemek menünüzü çok daha çekici yapar.',
    path: '/app/menu/products',
  },
  {
    id: 'campaigns',
    icon: Sparkles,
    title: 'İndirimler & Ödüller',
    description: 'Kampanyalar oluşturun: yüzde indirimi, sabit tutar, ücretsiz ürün veya puan çarpanı. Ödül sistemiyle müşterilerinizi puanlarıyla hediyeler kazanmaya teşvik edin.',
    tip: 'Kuponlar ile tek kullanımlık veya sınırlı kampanyalar da oluşturabilirsiniz.',
    path: '/app/campaigns',
  },
  {
    id: 'customers',
    icon: Users,
    title: 'Müşteriler',
    description: 'Tüm müşterilerinizi, sadakat puanlarını ve ziyaret geçmişlerini görün. Müşteri detaylarına tıklayarak profil, sipariş geçmişi ve puan hareketlerini inceleyin.',
    tip: 'Müşteri kayıtları sayfasından check-in ve sipariş loglarını takip edin.',
    path: '/app/customers',
  },
  {
    id: 'analytics',
    icon: BarChart3,
    title: 'Analitik',
    description: 'İşletmenizin performansını grafiklerle analiz edin. Satış trendleri, popüler ürünler, müşteri davranışları ve sadakat istatistikleri.',
    tip: 'Tarih aralığı filtreleriyle istediğiniz dönemi inceleyebilirsiniz.',
    path: '/app/analytics',
  },
  {
    id: 'notifications',
    icon: Bell,
    title: 'Bildirim & Duyurular',
    description: 'Kampanya bildirimlerini müşterilerinize gönderin. Duyurular ile özel haberler, etkinlikler veya menü güncellemelerini paylaşın.',
    tip: 'Hedef kitle seçerek bildirimleri sadece belirli müşteri gruplarına gönderebilirsiniz.',
    path: '/app/notifications',
  },
  {
    id: 'inventory',
    icon: Package,
    title: 'Stok Yönetimi',
    description: 'Ürün stoklarını takip edin, azalan stoklar için uyarı alın. Sınırsız stok seçeneğiyle bazı ürünleri stok takibinden muaf tutabilirsiniz.',
    tip: 'Kritik stok seviyesindeki ürünler otomatik olarak vurgulanır.',
    path: '/app/inventory',
  },
  {
    id: 'accounting',
    icon: Calculator,
    title: 'Muhasebe',
    description: 'Gelir-gider takibi yapın, günlük/haftalık/aylık mali raporlarınızı görüntüleyin. Ödeme yöntemlerine göre filtreleme yapabilirsiniz.',
    tip: 'Param entegrasyonu ile online ödemeleri otomatik takip edebilirsiniz.',
    path: '/app/accounting',
  },
  {
    id: 'settings',
    icon: Settings,
    title: 'İşletme Ayarları',
    description: 'İşletme profilinizi, logo ve banner\'ınızı düzenleyin. QR kod yönetimi, sadakat kuralları, ziyaret rolleri, kullanıcı yetkileri ve entegrasyonları bu bölümden yapılandırın.',
    tip: 'QR kodunuzu indirip masalarınıza yerleştirerek müşterilerinizin kolayca check-in yapmasını sağlayın.',
    path: '/app/settings',
  },
  {
    id: 'finish',
    icon: CheckCircle2,
    title: 'Hazırsınız!',
    description: 'Panelin tüm özelliklerini keşfettiniz. Artık işletmenizi ugra ile kolayca yönetebilirsiniz. Herhangi bir sorunuz olursa Destek sayfasından bize ulaşabilirsiniz.',
    tip: 'Sol menüden istediğiniz sayfaya hızlıca erişebilirsiniz.',
  },
];

const OnboardingGuide = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [direction, setDirection] = useState('next');

  const step = STEPS[currentStep];
  const isFirst = currentStep === 0;
  const isLast = currentStep === STEPS.length - 1;
  const Icon = step.icon;

  const animateStep = useCallback((newStep, dir) => {
    setDirection(dir);
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentStep(newStep);
      setIsAnimating(false);
    }, 200);
  }, []);

  const handleNext = () => {
    if (isLast) { onComplete(); return; }
    animateStep(currentStep + 1, 'next');
  };

  const handlePrev = () => {
    if (isFirst) return;
    animateStep(currentStep - 1, 'prev');
  };

  const handleFinish = () => onComplete();

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'Enter') handleNext();
      else if (e.key === 'ArrowLeft') handlePrev();
      else if (e.key === 'Escape') handleFinish();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentStep]);

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.6)',
      backdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 10000,
      padding: '1rem',
      animation: 'obFadeIn 0.3s ease',
    }}>
      <style>{`
        @keyframes obFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes obSlideNext { from { opacity: 0; transform: translateX(40px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes obSlidePrev { from { opacity: 0; transform: translateX(-40px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes obSlideOut { from { opacity: 1; } to { opacity: 0; } }
        @keyframes obPulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.05); } }
        @keyframes obIconFloat { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
        .ob-card {
          background: var(--bg-card, #fff);
          border: 1px solid var(--border, #e5e7eb);
          border-radius: 24px;
          width: 100%;
          max-width: 520px;
          box-shadow: 0 25px 60px rgba(0,0,0,0.3);
          overflow: hidden;
          position: relative;
        }
        .ob-content {
          animation: obSlideNext 0.3s ease;
        }
        .ob-content.animating { animation: obSlideOut 0.2s ease forwards; }
        .ob-content.from-prev { animation: obSlidePrev 0.3s ease; }
        .ob-dot {
          width: 8px; height: 8px; border-radius: 50%;
          background: var(--border, #d1d5db);
          transition: all 0.3s ease;
          cursor: pointer;
        }
        .ob-dot.active {
          background: #6f4e37;
          width: 24px; border-radius: 4px;
        }
        .ob-btn {
          padding: 12px 24px;
          border-radius: 14px;
          font-weight: 700;
          font-size: 0.9375rem;
          cursor: pointer;
          transition: all 0.2s ease;
          border: none;
          display: flex; align-items: center; gap: 8px;
        }
        .ob-btn:hover { transform: translateY(-1px); }
        .ob-btn-primary {
          background: #6f4e37;
          color: #fff;
          box-shadow: 0 4px 12px rgba(111, 78, 55, 0.3);
        }
        .ob-btn-primary:hover { box-shadow: 0 6px 20px rgba(111, 78, 55, 0.4); }
        .ob-btn-secondary {
          background: var(--bg-main, #f9fafb);
          color: var(--text-muted, #6b7280);
          border: 1px solid var(--border, #e5e7eb);
        }
        .ob-btn-secondary:hover { border-color: #6f4e37; color: #6f4e37; }
        .ob-btn-ghost {
          background: transparent;
          color: var(--text-muted, #6b7280);
          padding: 8px 16px;
          font-size: 0.875rem;
        }
        .ob-btn-ghost:hover { color: var(--text-main, #1f2937); }
        .ob-icon-wrap {
          width: 72px; height: 72px;
          border-radius: 20px;
          display: flex; align-items: center; justify-content: center;
          margin: 0 auto 1.5rem;
          animation: obIconFloat 3s ease-in-out infinite;
        }
        .ob-tip {
          background: rgba(111, 78, 55, 0.08);
          border-radius: 12px;
          padding: 12px 16px;
          font-size: 0.8125rem;
          color: #6f4e37;
          font-weight: 500;
          line-height: 1.5;
          display: flex; gap: 8px; align-items: flex-start;
        }
      `}</style>

      <div className="ob-card">
        {/* Progress bar */}
        <div style={{
          height: '4px',
          background: 'var(--border, #e5e7eb)',
          position: 'relative',
          overflow: 'hidden',
        }}>
          <div style={{
            height: '100%',
            width: `${((currentStep + 1) / STEPS.length) * 100}%`,
            background: 'linear-gradient(90deg, #6f4e37, #a0845c)',
            transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            borderRadius: '0 4px 4px 0',
          }} />
        </div>

        {/* Close / Skip button */}
        {!isLast && (
          <button
            onClick={handleFinish}
            className="ob-btn-ghost"
            style={{ position: 'absolute', top: '16px', right: '16px', zIndex: 1, padding: '8px' }}
          >
            <X size={18} />
          </button>
        )}

        {/* Content */}
        <div
          className={`ob-content ${isAnimating ? 'animating' : ''} ${direction === 'prev' && !isAnimating ? 'from-prev' : ''}`}
          style={{ padding: '2.5rem 2rem 1.5rem', textAlign: 'center' }}
        >
          {/* Step counter */}
          <div style={{
            fontSize: '0.75rem', fontWeight: 700,
            color: 'var(--text-muted, #9ca3af)',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            marginBottom: '1.25rem',
          }}>
            {currentStep + 1} / {STEPS.length}
          </div>

          {/* Icon */}
          <div className="ob-icon-wrap" style={{
            background: step.id === 'welcome' ? 'linear-gradient(135deg, #6f4e37, #a0845c)'
              : step.id === 'finish' ? 'linear-gradient(135deg, #059669, #34d399)'
              : 'rgba(111, 78, 55, 0.1)',
          }}>
            <Icon
              size={36}
              color={step.id === 'welcome' || step.id === 'finish' ? '#fff' : '#6f4e37'}
              strokeWidth={1.8}
            />
          </div>

          {/* Title */}
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: 800,
            color: 'var(--text-main, #1f2937)',
            marginBottom: '0.75rem',
            letterSpacing: '-0.02em',
          }}>
            {step.title}
          </h2>

          {/* Description */}
          <p style={{
            fontSize: '0.9375rem',
            color: 'var(--text-muted, #6b7280)',
            lineHeight: 1.7,
            marginBottom: '1.25rem',
            maxWidth: '420px',
            margin: '0 auto 1.25rem',
          }}>
            {step.description}
          </p>

          {/* Tip */}
          {step.tip && (
            <div className="ob-tip">
              <span style={{ flexShrink: 0, fontSize: '1rem' }}>💡</span>
              <span>{step.tip}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '1rem 2rem 2rem',
          display: 'flex', flexDirection: 'column', gap: '1.25rem',
        }}>
          {/* Dots */}
          <div style={{
            display: 'flex', gap: '6px',
            justifyContent: 'center', alignItems: 'center',
          }}>
            {STEPS.map((_, i) => (
              <div
                key={i}
                className={`ob-dot ${i === currentStep ? 'active' : ''}`}
                onClick={() => {
                  if (i !== currentStep) {
                    animateStep(i, i > currentStep ? 'next' : 'prev');
                  }
                }}
              />
            ))}
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
            {!isFirst && (
              <button className="ob-btn ob-btn-secondary" onClick={handlePrev}>
                <ChevronLeft size={18} />
                Geri
              </button>
            )}

            {isFirst ? (
              <button className="ob-btn ob-btn-primary" onClick={handleNext} style={{ animation: 'obPulse 2s ease-in-out infinite', padding: '14px 32px', fontSize: '1rem' }}>
                Başlayalım
                <ArrowRight size={20} />
              </button>
            ) : isLast ? (
              <button className="ob-btn ob-btn-primary" onClick={handleFinish} style={{ padding: '14px 32px', fontSize: '1rem', background: 'linear-gradient(135deg, #059669, #34d399)' }}>
                <CheckCircle2 size={20} />
                Paneli Kullanmaya Başla
              </button>
            ) : (
              <>
                <button className="ob-btn ob-btn-primary" onClick={handleNext}>
                  İleri
                  <ChevronRight size={18} />
                </button>
                <button className="ob-btn ob-btn-ghost" onClick={handleFinish}>
                  Bitir
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingGuide;
